// Pestaña "Alérgenos" de la ficha del presupuesto (formato nuevo, diseño A+).
//
// Flujo en tres pasos visibles: 1) quién es alérgico (se pega el correo del
// cliente o se escribe "3 cacahuete", se revisa la tabla y se añade; nombre de
// pila o número, nunca apellidos; casilla de lista definitiva), 2) menú de cada
// grupo de dieta (los que tienen el mismo perfil comen lo mismo: una sola
// elección de sustitutos por menú, un Aleatorio y una confirmación de cocina
// para todos; cada persona sigue con su bandeja y su etiqueta) y 3)
// confirmación de cocina (validar, historial y suplemento por menú adaptado).
//
// Además de los 14 alérgenos (garantizados), cada comensal puede tener dietas
// (vegetariano, vegano, sin cerdo, embarazada, sin lactosa): se tienen en
// cuenta sin garantía, cambian su menú igual que un alérgeno y se cobran con el
// suplemento por menú adaptado. Una fila puede ser un grupo sin nombres
// ("5 vegetarianos", `cantidad`) con bandeja individual o de grupo.
//
// Cada comensal come en "Todo el evento" (todos los menús de cocina) o en
// menús concretos. Todo el cálculo lo hace el backend (GET
// /presupuestos/{id}/alergenos, /alergenos/opciones e /alergenos/interpretar);
// aquí solo se muestra y se guarda. La pestaña nunca modifica el catálogo.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/api';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  ALERGENOS_OFICIALES,
  AVISO_EMBARAZADA,
  DIETAS,
  ESTILO_ESTADO_PLATO,
  TEXTO_ESTADO_LISTA,
  nombreAlergeno,
  personasComensal,
  siguienteNumeroComensal,
  textoDietas,
} from '../../utils/alergenosPresupuesto';
import {
  TEXTO_ALERGIA_SIN_REGISTRAR,
  mencionaAlergiaODieta,
  textoObservacionesPresupuesto,
} from '../../utils/tareasPresupuesto';
import { revisarNovedades } from '../guia/novedades';

const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const formatFechaHora = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return f.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// "A, B y C"
const listaTexto = (items) => {
  const v = (items || []).filter(Boolean);
  if (v.length <= 1) return v.join('');
  return `${v.slice(0, -1).join(', ')} y ${v[v.length - 1]}`;
};

// "Faltan 2 platos por confirmar en el grupo Celíacos"
const textoPendientesGrupo = (g) => `${g.pendientes === 1 ? 'Falta 1 plato' : `Faltan ${g.pendientes} platos`} por confirmar en el grupo ${g.nombre}`;

const FORM_VACIO = {
  etiqueta: '', alergenos_ids: [], trazas_aceptables: false, trazas_aceptables_motivo: '',
  alcance: 'evento', menus_indices: [], jornada_id: '', observaciones: '', restriccion_no_oficial: '',
  dietas: [], cantidad: 1, bandeja: 'individual',
};

// Texto breve: qué se garantiza y qué no
const TEXTO_GARANTIA = 'Los 14 alérgenos oficiales se garantizan. Las dietas (vegetariano, vegano, sin cerdo, embarazada, sin lactosa) se tienen en cuenta, sin garantía.';

// Etiqueta de una restricción fuera de los 14 alérgenos oficiales (ajo, cebolla,
// picante...): se tiene en cuenta en cocina, pero no se garantiza
const TEXTO_NO_GARANTIZADA = 'No garantizada';

// Menús de un comensal: por su linea_id (U-14: borrar o insertar una línea no
// los mueve), con respaldo a los índices en los documentos antiguos
const indicesDeComensal = (c, menus) => {
  const lids = Array.isArray(c?.menus_linea_ids) ? c.menus_linea_ids.filter(Boolean) : [];
  if (lids.length > 0) {
    const idx = lids.map(l => (menus || []).findIndex(m => m.linea_id === l)).filter(i => i >= 0);
    if (idx.length > 0) return [...new Set(idx)].sort((a, b) => a - b);
  }
  return c?.menus_indices || [];
};

// linea_id de esos menús (vacío si alguno no lo tiene: entonces van por índice)
const lineaIdsDeIndices = (indices, menus) => {
  const ids = (indices || []).map(i => menus?.[i]?.linea_id);
  return ids.length > 0 && ids.every(Boolean) ? ids : [];
};

// Comensal tal y como se envía al backend (las elecciones de platos y las
// confirmaciones de cocina son del grupo de dieta y no se mandan aquí)
const aEnvio = (c, menus) => {
  const indices = c.alcance === 'menus' ? indicesDeComensal(c, menus) : [];
  return {
    id: c.id || null,
    etiqueta: c.etiqueta,
    alergenos_ids: c.alergenos_ids || [],
    trazas_aceptables: !!c.trazas_aceptables,
    trazas_aceptables_motivo: c.trazas_aceptables ? (c.trazas_aceptables_motivo || '') : null,
    alcance: c.alcance === 'menus' ? 'menus' : 'evento',
    menus_indices: indices,
    menus_linea_ids: lineaIdsDeIndices(indices, menus),
    jornada_id: c.jornada_id || null,
    observaciones: c.observaciones || null,
    restriccion_no_oficial: c.restriccion_no_oficial || null,
    dietas: c.dietas || [],
    cantidad: personasComensal(c),
    bandeja: personasComensal(c) > 1 && c.bandeja === 'grupo' ? 'grupo' : 'individual',
  };
};

// Sustituciones actuales de un menú del grupo, tal y como las resolvió el ERP
// (los platos sin verificar que se quedan en el menú no llevan clave)
const sustitucionesDeMenu = (menu) => {
  const res = {};
  (menu?.platos || []).forEach((f) => {
    if (f.puede_comer || f.origen === 'pendiente_verificar') return;
    res[f.plato_id] = f.sustituto_id || null;
  });
  return res;
};

const Tarjeta = ({ titulo, accion, children, testid, guia }) => (
  <section className="bg-white border border-[#E1E6E3] rounded-xl p-4 sm:p-5" data-testid={testid} data-guia={guia}>
    {(titulo || accion) && (
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        {titulo && <h3 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">{titulo}</h3>}
        {accion}
      </div>
    )}
    {children}
  </section>
);

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const botonCocina = 'h-[30px] px-3 rounded-lg border border-[#1E4E79] bg-[#E6EFF8] text-[#1E4E79] text-xs font-bold whitespace-nowrap hover:bg-[#D6E6F5] disabled:opacity-50 disabled:cursor-not-allowed';
const botonEnlace = 'text-xs font-semibold text-[#11704F] hover:underline disabled:opacity-50';
const campo = 'w-full h-[34px] border border-[#CBD3CF] rounded-lg px-2 text-sm';

const ESTILO_PASO = {
  hecho: { clase: 'bg-[#E3F3EC] text-[#0F5E43]', texto: 'Hecho', borde: 'border-[#B9E0CE]' },
  pendiente: { clase: 'bg-[#FDF0DC] text-[#92400E]', texto: 'Pendiente', borde: 'border-[#F3D19B]' },
  espera: { clase: 'bg-[#EEF1EF] text-[#4A5A53]', texto: 'Después', borde: 'border-[#E1E6E3]' },
};

const GRUPOS_OPCIONES = [
  { clave: 'recomendados', titulo: 'Recomendados (menús parecidos)' },
  { clave: 'mismo_tipo', titulo: 'Mismo tipo' },
  { clave: 'otros', titulo: 'Otros tipos' },
];

// Texto del estado de un plato en el buscador de sustitutos
const textoOpcion = (o) => {
  if (!o.seleccionable) return o.motivo || 'No apto';
  if (o.estado === 'trazas') return 'Trazas aceptadas';
  if (o.estado === 'sin_verificar') return o.motivo || 'Falta que cocina lo confirme';
  return (ESTILO_ESTADO_PLATO[o.estado] || ESTILO_ESTADO_PLATO.apto).texto;
};

// Chips de los 14 alérgenos (formulario y tabla del texto interpretado)
const ChipsAlergenos = ({ alergenos, seleccionados, onToggle, pequenos = false }) => (
  <div className="flex flex-wrap gap-1.5">
    {alergenos.map((a) => {
      const activo = seleccionados.includes(a.id);
      return (
        <button
          key={a.id}
          type="button"
          onClick={() => onToggle(a.id)}
          aria-pressed={activo}
          className={`${pequenos ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'} rounded-full font-semibold border transition-colors ${
            activo ? 'bg-[#8A1C12] border-[#8A1C12] text-white' : 'bg-white border-[#CBD3CF] text-[#33443C] hover:bg-gray-50'
          }`}
        >
          {a.nombre}
        </button>
      );
    })}
  </div>
);

// Chips de las dietas (sin garantía), en otro color que los alérgenos
const ChipsDietas = ({ dietas, seleccionadas, onToggle, pequenos = false }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="alergenos-chips-dietas">
    {dietas.map((d) => {
      const activo = seleccionadas.includes(d.id);
      return (
        <button
          key={d.id}
          type="button"
          onClick={() => onToggle(d.id)}
          aria-pressed={activo}
          data-dieta={d.id}
          className={`${pequenos ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'} rounded-full font-semibold border transition-colors ${
            activo ? 'bg-[#9A3412] border-[#9A3412] text-white' : 'bg-white border-[#CBD3CF] text-[#33443C] hover:bg-gray-50'
          }`}
        >
          {d.nombre}
        </button>
      );
    })}
  </div>
);

// Aviso de una línea (ámbar), p. ej. el de embarazada
const AvisoAmbar = ({ texto, testid = 'alergenos-aviso-dieta' }) => (
  <p className="text-xs font-semibold text-[#7A3A06] bg-[#FDF0DC] border border-[#F3D19B] rounded-lg px-2.5 py-1.5" data-testid={testid}>
    {texto}
  </p>
);

// Alternar una dieta: vegano ya incluye vegetariano (el servidor no guarda los dos)
const alternarDieta = (actuales, id) => {
  const lista = actuales || [];
  if (lista.includes(id)) return lista.filter(d => d !== id);
  const nuevas = [...lista, id];
  const sinRedundancia = id === 'vegano' ? nuevas.filter(d => d !== 'vegetariano')
    : (id === 'vegetariano' ? nuevas.filter(d => d !== 'vegano') : nuevas);
  return DIETAS.map(d => d.id).filter(d => sinRedundancia.includes(d));
};

// Buscador de sustituto: todos los platos de cocina, agrupados y con su estado
// para el grupo. Los que contienen su alérgeno se ven pero no se pueden elegir.
const BuscadorSustituto = ({ fila, grupo, estadoOpciones, onElegir, onCerrar, deshabilitado }) => {
  const opciones = estadoOpciones?.datos?.opciones || [];
  return (
    <div className="mt-2 border border-[#CBD3CF] rounded-lg bg-white shadow-sm" data-testid="alergenos-buscador">
      <Command>
        <CommandInput placeholder="Busca un plato por su nombre..." aria-label={`Buscar sustituto de ${fila.nombre} para ${grupo.nombre}`} />
        <CommandList className="max-h-[320px]">
          <CommandEmpty>Ningún plato con ese nombre.</CommandEmpty>
          <CommandGroup heading="Qué hacer con este plato">
            {fila.estado === 'sin_verificar' && (
              <CommandItem value="dejarlo en su menu cocina lo confirma" disabled={deshabilitado} onSelect={() => onElegir('__mantener')} data-testid="alergenos-opcion-mantener">
                Dejarlo en su menú (cocina tiene que confirmarlo)
              </CommandItem>
            )}
            <CommandItem value="sin sustituto se retira" disabled={deshabilitado} onSelect={() => onElegir('')} data-testid="alergenos-opcion-retirar">
              Sin sustituto: se retira
            </CommandItem>
          </CommandGroup>
          {estadoOpciones?.cargando && <p className="px-3 py-2 text-xs text-[#5B6B64]">Cargando todos los platos...</p>}
          {estadoOpciones?.error && <p className="px-3 py-2 text-xs text-[#8A1C12]">{estadoOpciones.error}</p>}
          {GRUPOS_OPCIONES.map((g) => {
            const delGrupo = opciones.filter(o => o.grupo === g.clave);
            if (delGrupo.length === 0) return null;
            return (
              <CommandGroup key={g.clave} heading={`${g.titulo} · ${delGrupo.length}`}>
                {delGrupo.map((o) => {
                  const estilo = o.seleccionable
                    ? (ESTILO_ESTADO_PLATO[o.estado] || ESTILO_ESTADO_PLATO.apto)
                    : ESTILO_ESTADO_PLATO.contiene;
                  const elegido = fila.sustituto_id === o.id;
                  return (
                    <CommandItem
                      key={o.id}
                      value={`${o.nombre} ${o.id}`}
                      disabled={deshabilitado || !o.seleccionable}
                      onSelect={() => onElegir(o.id)}
                      className="flex items-center gap-2 text-xs"
                      data-testid="alergenos-opcion"
                      data-id={o.id}
                      data-estado={o.estado}
                      data-seleccionable={o.seleccionable ? 'si' : 'no'}
                    >
                      <span className={`flex-1 min-w-0 truncate ${elegido ? 'font-bold' : ''}`}>
                        {o.nombre}{elegido ? ' (elegido)' : ''}
                      </span>
                      <span className={`shrink-0 px-1.5 py-0.5 rounded border font-semibold ${estilo.clase}`}>{textoOpcion(o)}</span>
                      {o.seleccionable && o.diferencia_coste != null && (
                        <span className="shrink-0 text-[#5B6B64] w-[88px] text-right">
                          {o.diferencia_coste >= 0 ? '+' : ''}{formatEuros(o.diferencia_coste)}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            );
          })}
        </CommandList>
      </Command>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#EEF1EF] px-3 py-2">
        <p className="text-[11px] text-[#5B6B64]">El cambio vale para todo el grupo. El importe es la diferencia de coste con el plato original. Los platos que no le valen se ven en gris con el motivo.</p>
        <button type="button" onClick={onCerrar} className={botonEnlace}>Cerrar</button>
      </div>
    </div>
  );
};

const PestanaAlergenos = ({ presupuestoId, presupuesto, correos = [], onActualizado }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null); // { tipo: 'ok' | 'error', texto }
  const [enCurso, setEnCurso] = useState(null);
  const [textoPegado, setTextoPegado] = useState('');
  const [propuesta, setPropuesta] = useState(null); // { filas, no_entendido, origen, aviso }
  const [form, setForm] = useState(FORM_VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [verFormulario, setVerFormulario] = useState(false);
  // "Sí, hay alérgicos" en la pregunta inicial: abre la pantalla completa
  const [hayAlergicosElegido, setHayAlergicosElegido] = useState(false);
  const [verCorreo, setVerCorreo] = useState(false);
  const [correoElegido, setCorreoElegido] = useState('');
  const [verHistorial, setVerHistorial] = useState(false);
  const [verTabla, setVerTabla] = useState(false);
  const [buscadorAbierto, setBuscadorAbierto] = useState(null); // `${clave}|${menuIndex}|${platoId}`
  const [opciones, setOpciones] = useState({}); // clave → { cargando, error, datos }

  // Firma de lo que afecta al análisis: si cambia (se guarda algo), se recarga
  const firma = useMemo(() => JSON.stringify([
    presupuesto?.comensales_alergicos || [],
    presupuesto?.grupos_dieta || null,
    presupuesto?.alergenos_lista || null,
    presupuesto?.num_personas,
    (presupuesto?.menus_personalizados || []).map(m => [m.menu_id, m.num_personas, m.platos_ids, m.nombre_menu]),
    (presupuesto?.suplementos || []).filter(s => s.es_suplemento_menu_adaptado).map(s => [s.id, s.unidades, s.total]),
    presupuesto?.suplemento_menu_adaptado_regalado || null,
    // Multievento (M-100): lo que cambia en las jornadas también recarga
    // (personas, platos, unidades por plato y adaptaciones de cada línea)
    (presupuesto?.jornadas || []).filter(Boolean).map(j => [
      j.id, j.fecha, j.num_personas,
      (j.menus_personalizados || []).filter(Boolean).map(m => [
        m.menu_id, m.linea_id, m.num_personas, m.platos_ids, m.unidades_por_plato, m.adaptaciones,
      ]),
    ]),
  ]), [presupuesto]);

  const cargar = useCallback(async () => {
    if (!presupuestoId) return;
    try {
      const { data } = await api.get(`/presupuestos/${presupuestoId}/alergenos`);
      setDatos(data && Array.isArray(data.comensales) ? data : null);
      setError(null);
    } catch (err) {
      console.error('Error cargando alérgenos del presupuesto:', err);
      setError(err.response?.data?.detail || 'No se pudo cargar el análisis de alérgenos.');
    } finally {
      setCargando(false);
    }
  }, [presupuestoId]);

  useEffect(() => { cargar(); }, [cargar, firma]);

  // Las opciones dependen de las confirmaciones y los alérgenos: se piden de nuevo tras cada cambio
  useEffect(() => { setOpciones({}); }, [datos]);

  const comensales = useMemo(() => datos?.comensales || [], [datos]);
  const grupos = useMemo(() => datos?.grupos || [], [datos]);
  const menus = useMemo(() => datos?.menus || [], [datos]);
  // Novedades: la zona de sustituciones aparece sin cambiar de ruta (al cargar
  // los datos o con la ficha antigua): se avisa para que mire si hay alguna
  const hayZonaSustitutos = comensales.length > 0 && grupos.length > 0;
  useEffect(() => {
    if (!hayZonaSustitutos) return undefined;
    const t = setTimeout(revisarNovedades, 300);
    return () => clearTimeout(t);
  }, [hayZonaSustitutos]);
  const menusCocina = useMemo(() => menus.filter(m => m.de_cocina !== false), [menus]);
  const alergenos = datos?.alergenos?.length ? datos.alergenos : ALERGENOS_OFICIALES;
  // Dietas (sin garantía) y el aviso de embarazada: los del servidor (un solo sitio)
  const dietasCatalogo = datos?.dietas?.length ? datos.dietas : DIETAS;
  const avisoEmbarazada = datos?.aviso_embarazada || AVISO_EMBARAZADA;
  const personasTotal = comensales.reduce((s, c) => s + personasComensal(c), 0);
  const jornadas = presupuesto?.es_multi_evento ? (presupuesto?.jornadas || []) : [];
  const lista = datos?.lista || { estado: 'sin_lista', historial: [] };
  const grupoPorComensal = useMemo(() => {
    const m = {};
    grupos.forEach((g) => (g.comensales || []).forEach((c) => { m[c.id] = g; }));
    return m;
  }, [grupos]);
  const grupoPorClave = useMemo(() => {
    const m = {};
    grupos.forEach((g) => { m[g.clave] = g; });
    return m;
  }, [grupos]);

  const ejecutar = async (clave, fn, textoOk) => {
    setEnCurso(clave);
    setAviso(null);
    try {
      await fn();
      if (onActualizado) await onActualizado();
      await cargar();
      if (textoOk) setAviso({ tipo: 'ok', texto: textoOk });
      return true;
    } catch (err) {
      console.error('Error en la pestaña Alérgenos:', err);
      setAviso({ tipo: 'error', texto: err.response?.data?.detail || 'No se pudo guardar.' });
      return false;
    } finally {
      setEnCurso(null);
    }
  };

  const guardarComensales = (nuevos, textoOk, extra = {}) => ejecutar(
    'comensales',
    () => api.put(`/presupuestos/${presupuestoId}/alergenos/comensales`, { comensales: nuevos.map(c => aEnvio(c, menus)), ...extra }),
    textoOk,
  );

  const nombreMenu = (idx) => (menus[idx]?.nombre || `Menú ${Number(idx) + 1}`);
  const textoAlcance = (c) => {
    if (c.alcance !== 'menus' || menusCocina.length <= 1) return 'Todo el evento';
    return indicesDeComensal(c, menus).map(nombreMenu).join(', ') || 'Todo el evento';
  };

  // ----- Texto pegado del correo (o sintaxis corta) -----
  const handleInterpretar = async () => {
    const texto = textoPegado.trim();
    if (!texto) {
      setAviso({ tipo: 'error', texto: 'Pega el texto del correo o escribe, por ejemplo, "3 cacahuete".' });
      return;
    }
    setEnCurso('interpretar');
    setAviso(null);
    try {
      const { data } = await api.post(`/presupuestos/${presupuestoId}/alergenos/interpretar`, { texto });
      const filas = (data?.filas || []).map((f, i) => ({ ...f, clave: `fila-${Date.now()}-${i}` }));
      setPropuesta({ filas, no_entendido: data?.no_entendido || [], origen: data?.origen, aviso: data?.aviso || null });
      if (filas.length === 0) setAviso({ tipo: 'error', texto: 'No he encontrado ningún comensal con alergias o dietas en el texto.' });
    } catch (err) {
      console.error('Error interpretando el texto de alergias:', err);
      setAviso({ tipo: 'error', texto: err.response?.data?.detail || 'No se pudo interpretar el texto.' });
    } finally {
      setEnCurso(null);
    }
  };

  const cambiarFila = (clave, cambios) => setPropuesta(prev => (prev ? {
    ...prev,
    filas: prev.filas.map(f => (f.clave === clave ? { ...f, ...cambios } : f)),
  } : prev));

  const quitarFila = (clave) => setPropuesta(prev => (prev ? { ...prev, filas: prev.filas.filter(f => f.clave !== clave) } : prev));

  const filasIncluidas = (propuesta?.filas || []).filter(f => f.incluir);
  const personasPropuesta = filasIncluidas.reduce((s, f) => s + Math.max(1, Number(f.cantidad) || 1), 0);

  const handleAnadirPropuesta = async () => {
    if (filasIncluidas.length === 0) {
      setAviso({ tipo: 'error', texto: 'Marca al menos una fila para añadir.' });
      return;
    }
    const sinMotivo = filasIncluidas.find(f => f.trazas_aceptables && !String(f.trazas_aceptables_motivo || '').trim());
    if (sinMotivo) {
      setAviso({ tipo: 'error', texto: 'Indica el motivo de aceptar trazas (qué dijo el cliente) en las filas que lo tienen marcado.' });
      return;
    }
    let n = siguienteNumeroComensal(comensales);
    const nuevos = [];
    filasIncluidas.forEach((f) => {
      const cantidad = Math.min(200, Math.max(1, parseInt(f.cantidad, 10) || 1));
      const etiquetaFila = String(f.etiqueta || '').trim().split(/\s+/)[0] || '';
      const alcanceEvento = f.alcance === 'evento' || f.alcance === undefined || f.alcance === '';
      const dietasFila = f.dietas || [];
      // Solo dietas ("5 vegetarianos"): UNA fila con sus personas (sin nombres;
      // el servidor pone "5 vegetarianos" si no hay nombre). Con alérgenos, una
      // fila por persona, como siempre.
      if (dietasFila.length > 0 && !(f.alergenos_ids || []).length) {
        nuevos.push({
          etiqueta: (cantidad > 1 ? String(f.etiqueta || '').trim() : etiquetaFila).slice(0, 40),
          alergenos_ids: [],
          dietas: dietasFila,
          cantidad,
          bandeja: cantidad > 1 && f.bandeja === 'grupo' ? 'grupo' : 'individual',
          trazas_aceptables: false,
          trazas_aceptables_motivo: '',
          alcance: alcanceEvento ? 'evento' : 'menus',
          menus_indices: alcanceEvento ? [] : [Number(f.alcance)],
          menus_linea_ids: alcanceEvento ? [] : lineaIdsDeIndices([Number(f.alcance)], menus),
          observaciones: String(f.observaciones || '').trim(),
        });
        return;
      }
      for (let i = 0; i < cantidad; i += 1) {
        let etiqueta = etiquetaFila;
        if (!etiqueta || cantidad > 1) {
          etiqueta = `Persona ${n}`;
          n += 1;
        }
        nuevos.push({
          etiqueta: etiqueta.slice(0, 40),
          alergenos_ids: f.alergenos_ids || [],
          dietas: dietasFila,
          trazas_aceptables: !!f.trazas_aceptables,
          trazas_aceptables_motivo: String(f.trazas_aceptables_motivo || '').trim(),
          alcance: f.alcance === 'evento' || f.alcance === undefined || f.alcance === '' ? 'evento' : 'menus',
          menus_indices: f.alcance === 'evento' || f.alcance === undefined || f.alcance === '' ? [] : [Number(f.alcance)],
          menus_linea_ids: f.alcance === 'evento' || f.alcance === undefined || f.alcance === '' ? [] : lineaIdsDeIndices([Number(f.alcance)], menus),
          observaciones: String(f.observaciones || '').trim(),
        });
      }
    });
    const personasNuevas = nuevos.reduce((s, c) => s + personasComensal(c), 0);
    const ok = await guardarComensales(
      [...comensales, ...nuevos],
      `${plural(personasNuevas, 'comensal añadido', 'comensales añadidos')}.`,
      { desde_texto: true },
    );
    if (ok) {
      setPropuesta(null);
      setTextoPegado('');
    }
  };

  // ----- Alta / edición individual -----
  const empezarEdicion = (c) => {
    setEditandoId(c.id);
    setVerFormulario(true);
    setForm({
      etiqueta: c.etiqueta || '',
      alergenos_ids: c.alergenos_ids || [],
      trazas_aceptables: !!c.trazas_aceptables,
      trazas_aceptables_motivo: c.trazas_aceptables_motivo || '',
      alcance: c.alcance === 'menus' ? 'menus' : 'evento',
      menus_indices: c.alcance === 'menus' ? indicesDeComensal(c, menus) : [],
      jornada_id: c.jornada_id || '',
      observaciones: c.observaciones || '',
      restriccion_no_oficial: c.restriccion_no_oficial || '',
      dietas: c.dietas || [],
      cantidad: personasComensal(c),
      bandeja: c.bandeja === 'grupo' ? 'grupo' : 'individual',
    });
  };
  const cancelarEdicion = () => { setEditandoId(null); setForm(FORM_VACIO); setVerFormulario(false); };

  const toggleAlergenoForm = (id) => {
    setForm(prev => ({
      ...prev,
      alergenos_ids: prev.alergenos_ids.includes(id)
        ? prev.alergenos_ids.filter(a => a !== id)
        : [...prev.alergenos_ids, id],
    }));
  };

  const toggleMenuForm = (idx) => {
    setForm(prev => ({
      ...prev,
      menus_indices: prev.menus_indices.includes(idx)
        ? prev.menus_indices.filter(i => i !== idx)
        : [...prev.menus_indices, idx].sort((a, b) => a - b),
    }));
  };

  const handleGuardarComensal = async () => {
    const etiqueta = form.etiqueta.trim();
    const cantidad = Math.min(500, Math.max(1, parseInt(form.cantidad, 10) || 1));
    // Un grupo sin nombres o una fila solo con dietas puede ir sin nombre: el
    // servidor pone "5 vegetarianos", "Sin cerdo"...
    if (!etiqueta && !(form.dietas.length > 0 || cantidad > 1)) {
      setAviso({ tipo: 'error', texto: 'Escribe el nombre de pila o un número (sin apellidos).' });
      return;
    }
    if (form.trazas_aceptables && !form.trazas_aceptables_motivo.trim()) {
      setAviso({ tipo: 'error', texto: 'Indica el motivo de aceptar trazas (qué dijo el cliente).' });
      return;
    }
    if (form.alcance === 'menus' && form.menus_indices.length === 0) {
      setAviso({ tipo: 'error', texto: 'Elige al menos un menú o marca "Todo el evento".' });
      return;
    }
    const base = {
      etiqueta,
      alergenos_ids: ALERGENOS_OFICIALES.map(a => a.id).filter(id => form.alergenos_ids.includes(id)),
      trazas_aceptables: form.trazas_aceptables,
      trazas_aceptables_motivo: form.trazas_aceptables_motivo.trim(),
      alcance: form.alcance,
      menus_indices: form.alcance === 'menus' ? form.menus_indices : [],
      menus_linea_ids: form.alcance === 'menus' ? lineaIdsDeIndices(form.menus_indices, menus) : [],
      jornada_id: form.jornada_id || null,
      observaciones: form.observaciones.trim(),
      restriccion_no_oficial: form.restriccion_no_oficial.trim(),
      dietas: DIETAS.map(d => d.id).filter(id => form.dietas.includes(id)),
      cantidad,
      bandeja: cantidad > 1 && form.bandeja === 'grupo' ? 'grupo' : 'individual',
    };
    // Si cambian sus alérgenos, sus dietas, sus trazas o sus menús, pasa a otro
    // grupo de dieta y el backend le deja las elecciones que sigan valiendo
    const nuevos = editandoId
      ? comensales.map(c => (c.id === editandoId ? { ...c, ...base } : c))
      : [...comensales, base];
    const ok = await guardarComensales(nuevos, editandoId ? 'Comensal actualizado.' : 'Comensal añadido.');
    if (ok) cancelarEdicion();
  };

  const handleEliminar = async (c) => {
    if (!window.confirm(`¿Quitar a ${c.etiqueta} de la lista de alergias y dietas?`)) return;
    await guardarComensales(comensales.filter(x => x.id !== c.id), 'Comensal quitado.');
    if (editandoId === c.id) cancelarEdicion();
  };

  // ----- Adaptaciones que se quedaron sin comensal (cabecera y jornadas) -----
  const handleLimpiarHuerfanas = () => ejecutar(
    'limpiar',
    () => api.post(`/presupuestos/${presupuestoId}/alergenos/limpiar`),
    'Adaptaciones sin comensal quitadas.',
  );

  // ----- Sustituciones del grupo -----
  // `manuales` ({menu: [plato]}): lo decidido a mano ahora, que deja de ser
  // propuesta del ERP aunque sea el mismo plato
  const guardarSustituciones = (grupo, porMenu, textoOk, manuales = {}) => ejecutar(
    `grupo-${grupo.clave}`,
    () => api.post(`/presupuestos/${presupuestoId}/alergenos/grupo-sustituciones`, { clave: grupo.clave, sustituciones: porMenu, manuales }),
    textoOk,
  );

  const handleSustitucion = async (grupo, menu, platoId, valor) => {
    const sust = sustitucionesDeMenu(menu);
    if (valor === '__mantener') delete sust[platoId];
    else sust[platoId] = valor || null;
    // Lo elegido o retirado aquí es una decisión a mano (los demás platos
    // siguen como estaban); "lo decide el ERP" vuelve a ser propuesta
    const manuales = valor === '__mantener' ? {} : { [menu.menu_index]: [platoId] };
    const ok = await guardarSustituciones(grupo, { [menu.menu_index]: sust }, null, manuales);
    if (ok) setBuscadorAbierto(null);
  };

  const abrirBuscador = async (grupo, menu, platoId) => {
    const clave = `${grupo.clave}|${menu.menu_index}|${platoId}`;
    if (buscadorAbierto === clave) { setBuscadorAbierto(null); return; }
    setBuscadorAbierto(clave);
    if (opciones[clave]?.datos || opciones[clave]?.cargando) return;
    setOpciones(prev => ({ ...prev, [clave]: { cargando: true } }));
    try {
      const { data } = await api.get(`/presupuestos/${presupuestoId}/alergenos/opciones`, {
        params: { grupo: grupo.clave, menu_index: menu.menu_index, plato_id: platoId },
      });
      setOpciones(prev => ({ ...prev, [clave]: { datos: data && Array.isArray(data.opciones) ? data : { opciones: [] } } }));
    } catch (err) {
      console.error('Error cargando los platos para sustituir:', err);
      setOpciones(prev => ({ ...prev, [clave]: { error: err.response?.data?.detail || 'No se pudieron cargar los platos.' } }));
    }
  };

  // Cambia al azar los sustitutos de todos los menús del grupo: elige entre los
  // platos compatibles del mismo tipo, primero los de los menús parecidos.
  const handleAleatorio = async (grupo) => {
    const porMenu = {};
    let cambiados = 0;
    (grupo.por_menu || []).forEach((menu) => {
      const sust = sustitucionesDeMenu(menu);
      const usados = new Set();
      (menu.platos || []).filter(f => !f.puede_comer).forEach((f) => {
        const sugerencias = f.sugerencias || [];
        const deHermanos = sugerencias.filter(o => o.de_menus_hermanos);
        let pool = (deHermanos.length > 0 ? deHermanos : sugerencias).filter(o => !usados.has(o.id));
        if (pool.length > 1 && f.sustituto_id) pool = pool.filter(o => o.id !== f.sustituto_id);
        if (pool.length === 0) return;
        const elegido = pool[Math.floor(Math.random() * pool.length)];
        usados.add(elegido.id);
        if (sust[f.plato_id] !== elegido.id) cambiados += 1;
        sust[f.plato_id] = elegido.id;
      });
      porMenu[menu.menu_index] = sust;
    });
    if (cambiados === 0) {
      setAviso({ tipo: 'error', texto: 'No hay otras opciones compatibles del mismo tipo para estos menús.' });
      return;
    }
    await guardarSustituciones(grupo, porMenu, `Menú de ${grupo.nombre} cambiado al azar (para todo el grupo).`);
  };

  // ----- Confirmación a mano de cocina (por grupo de dieta) -----
  const describirGrupos = (claves) => listaTexto((claves || []).map((k) => {
    const g = grupoPorClave[k];
    if (!g) return null;
    return `${g.nombre} (${listaTexto((g.comensales || []).map(c => c.etiqueta))})`;
  }));

  const handleConfirmarCocina = async (platoId, nombrePlato, claves) => {
    const quienes = describirGrupos(claves);
    if (!window.confirm(`¿Cocina confirma que ${nombrePlato} es seguro para ${quienes}?\n\nVale para todo el grupo. Quedará registrado a tu nombre, con fecha y hora. El catálogo no se cambia.`)) return;
    await ejecutar(
      `confirmar-${platoId}`,
      () => api.post(`/presupuestos/${presupuestoId}/alergenos/confirmar-plato`, { plato_id: platoId, accion: 'confirmar', grupos: claves }),
      `${nombrePlato}: confirmado a mano por cocina.`,
    );
  };

  const handleQuitarConfirmacion = async (platoId, nombrePlato, claves) => {
    if (!window.confirm(`¿Quitar la confirmación de cocina de ${nombrePlato}? Volverá a quedar pendiente.`)) return;
    await ejecutar(
      `quitar-${platoId}`,
      () => api.post(`/presupuestos/${presupuestoId}/alergenos/confirmar-plato`, { plato_id: platoId, accion: 'quitar', grupos: claves }),
      'Confirmación quitada.',
    );
  };

  // ----- Estado de la lista -----
  const handleEstado = (accion, extra = {}) => ejecutar(
    accion,
    () => api.post(`/presupuestos/${presupuestoId}/alergenos/estado`, { accion, ...extra }),
    {
      recibida: extra.correo_id !== undefined ? 'Correo del cliente vinculado.' : 'Lista marcada como definitiva.',
      no_recibida: 'Lista desmarcada: vuelve a estar pendiente del cliente.',
      validar_cocina: 'Lista validada por cocina.',
      reabrir: lista.estado === 'sin_alergicos' ? 'Se deshace Sin alérgicos.' : 'Lista reabierta.',
      sin_alergicos: 'Marcado sin alérgicos: la tarea queda hecha.',
    }[accion],
  );

  const handleCasillaDefinitiva = async (marcada) => {
    if (marcada) {
      await handleEstado('recibida');
      return;
    }
    const txt = lista.estado === 'validada_cocina'
      ? '¿Desmarcar la lista definitiva? Ya estaba validada por cocina: volverá a estar pendiente y habrá que validarla otra vez.'
      : '¿Desmarcar la lista definitiva? Volverá a estar pendiente del cliente.';
    if (!window.confirm(txt)) return;
    await handleEstado('no_recibida');
  };

  // ----- Suplemento -----
  const handleSuplemento = (accion) => ejecutar(
    `sup-${accion}`,
    () => api.post(`/presupuestos/${presupuestoId}/suplemento-menu-adaptado`, { accion }),
    { anadir: 'Suplemento por menú adaptado añadido a las líneas.', regalar: 'Suplemento regalado (queda registrado).', quitar_regalo: null }[accion],
  );

  if (cargando && !datos) {
    return <Tarjeta testid="alergenos-cargando"><p className="text-sm text-[#5B6B64]">Cargando alérgenos...</p></Tarjeta>;
  }
  if (!datos) {
    return (
      <Tarjeta testid="alergenos-error">
        <p className="text-sm text-[#8A1C12]">{error || 'No hay datos de alérgenos.'}</p>
        <button type="button" onClick={cargar} className={`${botonSecundario} mt-3`}>Reintentar</button>
      </Tarjeta>
    );
  }

  const hayComensales = comensales.length > 0;
  // Observaciones (generales o de una jornada) que hablan de alergias o dietas
  // sin nada registrado: aviso para no perderlas
  const hayAdaptacionesEnMenus = [
    ...(presupuesto?.menus_personalizados || []),
    ...((presupuesto?.jornadas || []).flatMap(j => (j && j.menus_personalizados) || [])),
  ].some(m => m && Array.isArray(m.adaptaciones) && m.adaptaciones.length > 0);
  // El cliente ha confirmado que no hay alérgicos: nada pendiente en la pestaña
  const sinAlergicos = lista.estado === 'sin_alergicos';
  const alergiaSinRegistrar = !hayComensales && !hayAdaptacionesEnMenus && !sinAlergicos
    && mencionaAlergiaODieta(textoObservacionesPresupuesto(presupuesto));
  const validada = lista.estado === 'validada_cocina';
  const definitiva = lista.estado !== 'sin_lista' && !sinAlergicos;
  const pendientesCocina = datos.platos_sin_verificar || [];
  const confirmadosCocina = datos.platos_confirmados_cocina || [];
  const gruposPendientes = grupos.filter(g => (g.pendientes || 0) > 0);
  const textoPendientes = gruposPendientes.map(textoPendientesGrupo).join('; ');
  const correoVinculado = lista.correo_id ? correos.find(c => c.id === lista.correo_id) : null;
  const totalCambios = (datos.resumen_por_menu || []).reduce((s, r) => s + (r.sustituciones || 0), 0);
  const totalRetirados = (datos.resumen_por_menu || []).reduce((s, r) => s + (r.retirados || 0), 0);
  const variosMenus = menusCocina.length > 1;

  // ----- Pasos -----
  const pasos = (() => {
    if (sinAlergicos && !hayComensales) {
      const confirmado = `Sin alérgicos: confirmado${lista.sin_alergicos_por ? ` por ${lista.sin_alergicos_por}` : ''}${lista.sin_alergicos_en ? ` el ${formatFechaHora(lista.sin_alergicos_en).replace(/\/\d{4},?/, '')}` : ''}.`;
      return [
        { n: 1, titulo: 'Alergias y dietas', ancla: 'alergenos-paso-1', estado: 'hecho', texto: confirmado },
        { n: 2, titulo: 'Menú de cada grupo', ancla: 'alergenos-paso-2', estado: 'hecho', texto: 'No hace falta.' },
        { n: 3, titulo: 'Confirmación de cocina', ancla: 'alergenos-paso-3', estado: 'hecho', texto: 'No hace falta.' },
      ];
    }
    const quienes = `${plural(personasTotal, 'comensal', 'comensales')} en ${plural(grupos.length, 'grupo', 'grupos')}`;
    const p1 = !hayComensales
      ? { estado: 'pendiente', texto: 'Pega el correo del cliente con las alergias o dietas (o escribe "3 cacahuete", "5 vegetarianos") y pulsa Interpretar. Si el cliente no ha comunicado nada, no hace falta nada.' }
      : (!definitiva
        ? { estado: 'pendiente', texto: `${quienes}. Cuando el cliente confirme la lista final, marca "El cliente ha confirmado que la lista es definitiva".` }
        : { estado: 'hecho', texto: `${quienes} · lista definitiva${lista.recibida_en ? ` desde el ${formatFechaHora(lista.recibida_en).slice(0, 10)}` : ''}.` });
    let p2;
    if (!hayComensales) p2 = { estado: 'espera', texto: 'Primero apunta a los comensales.' };
    else if (gruposPendientes.length > 0) {
      p2 = { estado: 'pendiente', texto: `${textoPendientes}: pulsa "Cocina lo confirma" o cámbialo por otro plato.` };
    } else {
      const partes = [];
      if (totalCambios) partes.push(plural(totalCambios, 'plato cambiado', 'platos cambiados'));
      if (totalRetirados) partes.push(plural(totalRetirados, 'retirado', 'retirados'));
      p2 = { estado: 'hecho', texto: partes.length ? `Cada grupo tiene su menú (los de la misma dieta comen lo mismo): ${partes.join(' y ')}.` : 'Todos pueden comer los menús tal cual.' };
    }
    let p3;
    if (validada && datos.huella_cambiada) p3 = { estado: 'pendiente', texto: 'Han cambiado las personas o los menús después de validar: pulsa "Reabrir" y vuelve a validar.' };
    else if (validada) p3 = { estado: 'hecho', texto: `Validado por ${lista.validada_por || 'cocina'} el ${formatFechaHora(lista.validada_en)}.` };
    else if (!hayComensales) p3 = { estado: 'espera', texto: 'Sin alergias ni dietas no hace falta validar.' };
    else if (gruposPendientes.length > 0) p3 = { estado: 'espera', texto: 'Primero termina el paso 2.' };
    else p3 = { estado: 'pendiente', texto: 'Todo listo: cuando cocina haya revisado los menús de cada grupo, pulsa "Validado por cocina".' };
    return [
      { n: 1, titulo: 'Alergias y dietas', ancla: 'alergenos-paso-1', ...p1 },
      { n: 2, titulo: 'Menú de cada grupo', ancla: 'alergenos-paso-2', ...p2 },
      { n: 3, titulo: 'Confirmación de cocina', ancla: 'alergenos-paso-3', ...p3 },
    ];
  })();

  const irA = (ancla) => {
    const el = typeof document !== 'undefined' ? document.getElementById(ancla) : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderPasos = () => (
    <ol className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="alergenos-pasos">
      {pasos.map((p) => {
        const e = ESTILO_PASO[p.estado];
        return (
          <li key={p.n}>
            <button
              type="button"
              onClick={() => irA(p.ancla)}
              className={`w-full h-full text-left bg-white border ${e.borde} rounded-xl p-3 hover:shadow-sm`}
              data-testid={`alergenos-paso-${p.n}-resumen`}
              data-estado={p.estado}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-[#16221D]">{p.n}. {p.titulo}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${e.clase}`}>{e.texto}</span>
              </span>
              <span className="block text-xs text-[#33443C] mt-1">{p.texto}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const renderCabeceraPaso = (n, titulo, subtitulo) => (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h2 className="text-base font-extrabold text-[#16221D]">{n}. {titulo}</h2>
      {subtitulo && <p className="text-xs text-[#5B6B64]">{subtitulo}</p>}
    </div>
  );

  // ----- Paso 1 -----
  const renderAlcanceForm = () => {
    if (!variosMenus) return null;
    return (
      <div>
        <p className="text-xs font-semibold text-[#33443C] mb-1">Dónde come</p>
        <div className="flex flex-wrap gap-4 text-sm text-[#16221D]">
          <label className="flex items-center gap-2">
            <input type="radio" name="alergenos-alcance" checked={form.alcance === 'evento'} onChange={() => setForm(prev => ({ ...prev, alcance: 'evento' }))} />
            Todo el evento
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="alergenos-alcance" checked={form.alcance === 'menus'} onChange={() => setForm(prev => ({ ...prev, alcance: 'menus' }))} />
            Solo algunos menús
          </label>
        </div>
        {form.alcance === 'menus' && (
          <div className="flex flex-wrap gap-3 mt-2" data-testid="alergenos-form-menus">
            {menusCocina.map(m => (
              <label key={m.index} className="flex items-center gap-1.5 text-xs text-[#16221D]">
                <input type="checkbox" checked={form.menus_indices.includes(m.index)} onChange={() => toggleMenuForm(m.index)} />
                {m.nombre}{m.hora ? ` (${m.hora})` : ''}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFormulario = () => (
    <div className="border border-[#E1E6E3] rounded-lg p-3 space-y-3 bg-[#FAFBFA]" data-testid="alergenos-form">
      <p className="text-sm font-semibold text-[#16221D]">{editandoId ? 'Editar comensal' : 'Añadir comensal'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="alergenos-etiqueta" className="block text-xs font-semibold text-[#33443C] mb-1">Nombre de pila o número</label>
          <input
            id="alergenos-etiqueta"
            type="text"
            maxLength={40}
            value={form.etiqueta}
            onChange={(e) => setForm(prev => ({ ...prev, etiqueta: e.target.value }))}
            placeholder={form.dietas.length > 0 || (parseInt(form.cantidad, 10) || 1) > 1 ? 'Opcional: "5 vegetarianos" si lo dejas vacío' : 'Marta, Persona 4...'}
            className="w-full h-[36px] border border-[#CBD3CF] rounded-lg px-3 text-sm"
          />
        </div>
        {jornadas.length > 0 && (
          <div>
            <label htmlFor="alergenos-jornada" className="block text-xs font-semibold text-[#33443C] mb-1">Jornada</label>
            <select
              id="alergenos-jornada"
              value={form.jornada_id}
              onChange={(e) => setForm(prev => ({ ...prev, jornada_id: e.target.value }))}
              className="w-full h-[36px] border border-[#CBD3CF] rounded-lg px-2 text-sm"
            >
              <option value="">Todas las jornadas</option>
              {jornadas.map((j, i) => <option key={j.id} value={j.id}>Jornada {i + 1}{j.fecha ? ` (${String(j.fecha).slice(0, 10)})` : ''}</option>)}
            </select>
          </div>
        )}
      </div>
      {renderAlcanceForm()}
      <div>
        <p className="text-xs font-semibold text-[#33443C] mb-1">Alérgenos <span className="font-normal text-[#5B6B64]">(garantizados)</span></p>
        <ChipsAlergenos alergenos={alergenos} seleccionados={form.alergenos_ids} onToggle={toggleAlergenoForm} />
      </div>
      <div>
        <p className="text-xs font-semibold text-[#33443C] mb-1">Dieta <span className="font-normal text-[#5B6B64]">(sin garantía)</span></p>
        <ChipsDietas
          dietas={dietasCatalogo}
          seleccionadas={form.dietas}
          onToggle={(id) => setForm(prev => ({ ...prev, dietas: alternarDieta(prev.dietas, id) }))}
        />
        {form.dietas.includes('embarazada') && <div className="mt-2"><AvisoAmbar texto={avisoEmbarazada} /></div>}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="alergenos-cantidad" className="block text-xs font-semibold text-[#33443C] mb-1">Personas</label>
          <input
            id="alergenos-cantidad"
            type="number"
            min={1}
            max={500}
            value={form.cantidad}
            onChange={(e) => setForm(prev => ({ ...prev, cantidad: e.target.value }))}
            className="w-[80px] h-[34px] border border-[#CBD3CF] rounded-lg px-2 text-sm"
            data-testid="alergenos-form-cantidad"
          />
        </div>
        {(parseInt(form.cantidad, 10) || 1) > 1 && (
          <div>
            <label htmlFor="alergenos-bandeja" className="block text-xs font-semibold text-[#33443C] mb-1">Bandeja</label>
            <select
              id="alergenos-bandeja"
              value={form.bandeja}
              onChange={(e) => setForm(prev => ({ ...prev, bandeja: e.target.value }))}
              className="h-[34px] border border-[#CBD3CF] rounded-lg px-2 text-sm"
              data-testid="alergenos-form-bandeja"
            >
              <option value="individual">Individual (una por persona)</option>
              <option value="grupo">De grupo (una para todos)</option>
            </select>
          </div>
        )}
        <p className="text-[11px] text-[#5B6B64] flex-1 min-w-[180px]">Varias personas sin nombres (por ejemplo, 5 vegetarianos) van en una sola fila.</p>
      </div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-[#16221D]">
          <input
            type="checkbox"
            checked={form.trazas_aceptables}
            onChange={(e) => setForm(prev => ({ ...prev, trazas_aceptables: e.target.checked }))}
          />
          Acepta trazas (solo intolerancias leves o preferencias, nunca alergias)
        </label>
        {form.trazas_aceptables && (
          <input
            type="text"
            value={form.trazas_aceptables_motivo}
            onChange={(e) => setForm(prev => ({ ...prev, trazas_aceptables_motivo: e.target.value }))}
            placeholder="Motivo: qué información dio el cliente"
            className="w-full h-[36px] border border-[#CBD3CF] rounded-lg px-3 text-sm"
            aria-label="Motivo de aceptar trazas"
          />
        )}
      </div>
      <div>
        <label htmlFor="alergenos-observaciones" className="block text-xs font-semibold text-[#33443C] mb-1">Observaciones</label>
        <input
          id="alergenos-observaciones"
          type="text"
          value={form.observaciones}
          onChange={(e) => setForm(prev => ({ ...prev, observaciones: e.target.value }))}
          placeholder="Mesa, preferencias..."
          className="w-full h-[36px] border border-[#CBD3CF] rounded-lg px-3 text-sm"
        />
      </div>
      <div>
        <label htmlFor="alergenos-restriccion" className="block text-xs font-semibold text-[#33443C] mb-1">
          Restricción no oficial <span className="ml-1 px-1.5 py-px rounded bg-[#FDF0DC] text-[#92400E] text-[11px] font-bold">{TEXTO_NO_GARANTIZADA}</span>
        </label>
        <input
          id="alergenos-restriccion"
          type="text"
          maxLength={200}
          value={form.restriccion_no_oficial}
          onChange={(e) => setForm(prev => ({ ...prev, restriccion_no_oficial: e.target.value }))}
          placeholder="No come ajo, sin cebolla, sin picante..."
          className="w-full h-[36px] border border-[#CBD3CF] rounded-lg px-3 text-sm"
          data-testid="alergenos-form-restriccion"
        />
        <p className="text-[11px] text-[#5B6B64] mt-1">Solo se garantizan los 14 alérgenos oficiales. Esto (como las dietas) se tiene en cuenta en cocina, pero no se puede garantizar.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!!enCurso} onClick={handleGuardarComensal} className={botonPrincipal} data-testid="alergenos-guardar-comensal">
          {enCurso === 'comensales' ? 'Guardando...' : (editandoId ? 'Guardar cambios' : 'Añadir comensal')}
        </button>
        <button type="button" onClick={cancelarEdicion} className={botonSecundario}>Cancelar</button>
      </div>
    </div>
  );

  const renderPropuesta = () => {
    if (!propuesta) return null;
    return (
      <div className="border border-[#CBD3CF] rounded-lg p-3 space-y-3 bg-[#FAFBFA]" data-testid="alergenos-propuesta">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-semibold text-[#16221D]">Revisa lo que he entendido antes de añadirlo</p>
          <p className="text-[11px] text-[#5B6B64]">Corrige lo que haga falta. Solo nombre de pila: los apellidos no se guardan.</p>
        </div>
        <p className="text-[11px] text-[#5B6B64]">{TEXTO_GARANTIA}</p>
        {propuesta.aviso && <p className="text-xs text-[#92400E] bg-[#FDF0DC] rounded-lg px-3 py-2">{propuesta.aviso}</p>}
        {propuesta.filas.length > 0 && (
          <ul className="space-y-2">
            {propuesta.filas.map((f) => (
              <li key={f.clave} className={`border rounded-lg p-2.5 space-y-2 ${f.incluir ? 'border-[#CBD3CF] bg-white' : 'border-[#E1E6E3] bg-[#F4F6F5]'}`} data-testid="alergenos-propuesta-fila">
                <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_90px_1fr_auto] gap-2 items-center">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-[#16221D]">
                    <input type="checkbox" checked={!!f.incluir} onChange={(e) => cambiarFila(f.clave, { incluir: e.target.checked })} aria-label="Incluir esta fila" />
                    Añadir
                  </label>
                  <input
                    type="text"
                    maxLength={40}
                    value={f.etiqueta || ''}
                    onChange={(e) => cambiarFila(f.clave, { etiqueta: e.target.value })}
                    placeholder={Number(f.cantidad) > 1 ? 'Grupo sin nombres (Persona N)' : 'Nombre de pila (o Persona N)'}
                    className={campo}
                    aria-label="Nombre de pila"
                  />
                  <label className="flex items-center gap-1.5 text-xs text-[#33443C]">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={f.cantidad}
                      onChange={(e) => cambiarFila(f.clave, { cantidad: e.target.value })}
                      className={`${campo} w-[60px]`}
                      aria-label="Número de personas"
                    />
                    pers.
                  </label>
                  <select
                    value={f.alcance === 'evento' || !f.alcance ? 'evento' : String(f.alcance)}
                    onChange={(e) => cambiarFila(f.clave, { alcance: e.target.value })}
                    className={campo}
                    aria-label="Dónde come"
                  >
                    <option value="evento">Todo el evento</option>
                    {variosMenus && menusCocina.map(m => <option key={m.index} value={String(m.index)}>Solo {m.nombre}</option>)}
                  </select>
                  <button type="button" onClick={() => quitarFila(f.clave)} className="text-xs font-semibold text-[#8A1C12] hover:underline">Quitar</button>
                </div>
                <ChipsAlergenos
                  alergenos={alergenos}
                  seleccionados={f.alergenos_ids || []}
                  pequenos
                  onToggle={(id) => cambiarFila(f.clave, {
                    alergenos_ids: (f.alergenos_ids || []).includes(id)
                      ? f.alergenos_ids.filter(a => a !== id)
                      : ALERGENOS_OFICIALES.map(a => a.id).filter(a => a === id || (f.alergenos_ids || []).includes(a)),
                  })}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-[#9A3412]">Dieta (sin garantía):</span>
                  <ChipsDietas
                    dietas={dietasCatalogo}
                    seleccionadas={f.dietas || []}
                    pequenos
                    onToggle={(id) => cambiarFila(f.clave, { dietas: alternarDieta(f.dietas, id) })}
                  />
                  {(f.dietas || []).length > 0 && !(f.alergenos_ids || []).length && Number(f.cantidad) > 1 && (
                    <select
                      value={f.bandeja === 'grupo' ? 'grupo' : 'individual'}
                      onChange={(e) => cambiarFila(f.clave, { bandeja: e.target.value })}
                      className="h-[26px] border border-[#CBD3CF] rounded-lg px-1.5 text-[11px]"
                      aria-label="Bandeja"
                    >
                      <option value="individual">Bandeja individual</option>
                      <option value="grupo">Bandeja de grupo</option>
                    </select>
                  )}
                </div>
                {(f.dietas || []).includes('embarazada') && <AvisoAmbar texto={avisoEmbarazada} />}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-[#16221D] whitespace-nowrap">
                      <input type="checkbox" checked={!!f.trazas_aceptables} onChange={(e) => cambiarFila(f.clave, { trazas_aceptables: e.target.checked })} />
                      Acepta trazas
                    </label>
                    {f.trazas_aceptables && (
                      <input
                        type="text"
                        value={f.trazas_aceptables_motivo || ''}
                        onChange={(e) => cambiarFila(f.clave, { trazas_aceptables_motivo: e.target.value })}
                        placeholder="Motivo: qué dijo el cliente"
                        className={campo}
                        aria-label="Motivo de aceptar trazas"
                      />
                    )}
                  </div>
                  <input
                    type="text"
                    value={f.observaciones || ''}
                    onChange={(e) => cambiarFila(f.clave, { observaciones: e.target.value })}
                    placeholder="Observaciones (kosher, sin picante...)"
                    className={campo}
                    aria-label="Observaciones"
                  />
                </div>
                {!(f.alergenos_ids || []).length && !(f.dietas || []).length && (
                  <p className="text-[11px] text-[#5B6B64]">Sin alérgeno oficial ni dieta. Márcala solo si quieres su bandeja aparte.</p>
                )}
                {!(f.alergenos_ids || []).length && (f.dietas || []).length > 0 && Number(f.cantidad) > 1 && (
                  <p className="text-[11px] text-[#5B6B64]">Se añade como un grupo de {f.cantidad} personas en una sola fila.</p>
                )}
              </li>
            ))}
          </ul>
        )}
        {propuesta.no_entendido.length > 0 && (
          <div className="text-xs text-[#92400E]" data-testid="alergenos-no-entendido">
            <p className="font-semibold">No he podido interpretar (revísalo y, si hace falta, añádelo a mano):</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              {propuesta.no_entendido.map(t => <li key={t}>{t}</li>)}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!!enCurso || personasPropuesta === 0} onClick={handleAnadirPropuesta} className={botonPrincipal} data-testid="alergenos-propuesta-anadir">
            {enCurso === 'comensales' ? 'Añadiendo...' : `Añadir ${plural(personasPropuesta, 'comensal', 'comensales')}`}
          </button>
          <button type="button" disabled={!!enCurso} onClick={() => setPropuesta(null)} className={botonSecundario}>Descartar</button>
        </div>
      </div>
    );
  };

  const renderComensales = () => (
    <Tarjeta titulo={`Comensales con alergias o dietas (${personasTotal})`} testid="alergenos-comensales">
      <div className="space-y-4">
        {/* Texto pegado del correo o sintaxis corta */}
        <div>
          <label htmlFor="texto-alergenos" className="block text-sm font-semibold text-[#16221D] mb-1">Pega aquí el correo del cliente</label>
          <textarea
            id="texto-alergenos"
            rows={4}
            value={textoPegado}
            onChange={(e) => setTextoPegado(e.target.value)}
            placeholder={'Por ejemplo:\nJuan: celíaco. María es intolerante a la lactosa. Pedro no come cerdo.\nO de forma corta: 3 cacahuete; 1 gluten; 5 vegetarianos'}
            className="w-full border border-[#CBD3CF] rounded-lg px-3 py-2 text-sm"
            data-testid="alergenos-texto"
          />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button type="button" disabled={!!enCurso || !textoPegado.trim()} onClick={handleInterpretar} className={botonPrincipal} data-testid="alergenos-interpretar">
              {enCurso === 'interpretar' ? 'Interpretando...' : 'Interpretar'}
            </button>
            <p className="text-xs text-[#5B6B64]">No se añade nada hasta que revises la tabla. Solo nombre de pila o número, nunca apellidos.</p>
          </div>
        </div>
        {renderPropuesta()}

        {/* Lista */}
        {hayComensales ? (
          <ul className="divide-y divide-[#EEF1EF] border border-[#E1E6E3] rounded-lg" data-testid="alergenos-lista-comensales">
            {comensales.map((c) => {
              const n = personasComensal(c);
              const nombresAl = (c.alergenos_ids || []).map(id => nombreAlergeno(id, alergenos));
              const nombresDi = (c.dietas || []).map(id => textoDietas([id], dietasCatalogo));
              const lineaAlcance = [
                variosMenus ? textoAlcance(c) : null,
                nombresAl.length > 0
                  ? (c.trazas_aceptables
                    ? `Acepta trazas (${c.trazas_aceptables_motivo || 'sin motivo'}${c.trazas_aceptables_por ? `, marcado por ${c.trazas_aceptables_por}` : ''})`
                    : 'No acepta trazas')
                  : null,
                c.jornada_id && jornadas.length > 0 ? `Jornada ${jornadas.findIndex(j => j.id === c.jornada_id) + 1}` : null,
              ].filter(Boolean);
              return (
              <li key={c.id} className="px-3 py-2.5 flex flex-wrap items-start justify-between gap-2" data-testid="alergenos-comensal">
                <div className="min-w-0">
                  {/* "5 · Vegetariano · bandeja de grupo", "Juan · Gluten · Sin lactosa" */}
                  <p className="text-sm text-[#16221D]" data-testid="alergenos-comensal-resumen">
                    <span className="font-bold">{n > 1 ? n : c.etiqueta}</span>
                    {nombresAl.map(t => <span key={`a-${t}`} className="font-semibold text-[#8A1C12]"> · {t}</span>)}
                    {nombresDi.map(t => <span key={`d-${t}`} className="font-semibold text-[#9A3412]"> · {t}</span>)}
                    {n > 1 && <span className="text-[#5B6B64]"> · {c.bandeja === 'grupo' ? 'bandeja de grupo' : 'bandejas individuales'}</span>}
                    {nombresAl.length === 0 && nombresDi.length === 0 && <span className="text-[#5B6B64]"> · Sin alérgenos ni dietas</span>}
                    {grupoPorComensal[c.id] && (grupoPorComensal[c.id].comensales || []).length > 1 && (
                      <span className="font-normal text-xs text-[#5B6B64]"> · grupo {grupoPorComensal[c.id].nombre}</span>
                    )}
                  </p>
                  {n > 1 && c.etiqueta && !String(c.etiqueta).startsWith(`${n} `) && (
                    <p className="text-xs text-[#5B6B64]">{c.etiqueta}</p>
                  )}
                  {nombresDi.length > 0 && <p className="text-[11px] text-[#9A3412]">Dieta: se tiene en cuenta, sin garantía</p>}
                  {lineaAlcance.length > 0 && <p className="text-xs text-[#5B6B64]">{lineaAlcance.join(' · ')}</p>}
                  {(c.dietas || []).includes('embarazada') && <div className="mt-1"><AvisoAmbar texto={avisoEmbarazada} /></div>}
                  {c.observaciones && <p className="text-xs text-[#33443C]">{c.observaciones}</p>}
                  {c.restriccion_no_oficial && (
                    <p className="text-xs text-[#92400E]" data-testid="alergenos-comensal-restriccion">
                      {c.restriccion_no_oficial}
                      <span className="ml-1.5 px-1.5 py-px rounded bg-[#FDF0DC] text-[11px] font-bold">{TEXTO_NO_GARANTIZADA}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-3 text-sm">
                  <button type="button" onClick={() => empezarEdicion(c)} className="font-semibold text-[#11704F] hover:underline">Editar</button>
                  <button type="button" disabled={!!enCurso} onClick={() => handleEliminar(c)} className="font-semibold text-[#8A1C12] hover:underline disabled:opacity-50">Quitar</button>
                </div>
              </li>
              );
            })}
          </ul>
        ) : sinAlergicos ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 bg-[#E3F3EC] text-[#0F5E43] text-sm" data-testid="alergenos-sin-alergicos-info">
            <p>
              Sin alérgicos: el cliente ha confirmado que no hay alérgicos.
              {(lista.sin_alergicos_por || lista.sin_alergicos_en) && (
                <span className="block text-xs">
                  Marcado{lista.sin_alergicos_por ? ` por ${lista.sin_alergicos_por}` : ''}{lista.sin_alergicos_en ? ` el ${formatFechaHora(lista.sin_alergicos_en)}` : ''}.
                </span>
              )}
            </p>
            <button type="button" disabled={!!enCurso} onClick={() => handleEstado('reabrir')} className={botonSecundario} data-testid="alergenos-deshacer-sin-alergicos">
              {enCurso === 'reabrir' ? 'Guardando...' : 'Deshacer'}
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-[#5B6B64]" data-testid="alergenos-sin-comensales">
              Todavía no hay comensales con alergias ni dietas. {TEXTO_GARANTIA}
            </p>
            {/* También con la lista marcada como recibida (sin comensales): el
                servidor lo permite salvo validada por cocina */}
            {lista.estado !== 'validada_cocina' && (
              <button
                type="button"
                disabled={!!enCurso}
                onClick={() => handleEstado('sin_alergicos')}
                className={botonSecundario}
                title="El cliente ha confirmado que no hay alérgicos"
                data-testid="alergenos-sin-alergicos"
              >
                {enCurso === 'sin_alergicos' ? 'Guardando...' : 'Sin alérgicos'}
              </button>
            )}
          </div>
        )}

        {/* Alta / edición individual (plegada para no competir con el texto pegado) */}
        {(verFormulario || editandoId) ? renderFormulario() : (
          <button type="button" onClick={() => setVerFormulario(true)} className="text-sm font-semibold text-[#11704F] hover:underline" data-testid="alergenos-abrir-form">
            Añadir a mano (nombre o grupo, alérgenos, dietas, trazas o menús)
          </button>
        )}
      </div>
    </Tarjeta>
  );

  const renderListaDefinitiva = () => (
    <Tarjeta titulo="Lista del cliente" testid="alergenos-lista-cliente">
      <div className="space-y-2 text-sm text-[#16221D]">
        <label className="flex items-start gap-2 font-semibold">
          <input
            type="checkbox"
            className="mt-1"
            checked={definitiva}
            disabled={!!enCurso || sinAlergicos}
            onChange={(e) => handleCasillaDefinitiva(e.target.checked)}
            data-testid="alergenos-lista-definitiva"
          />
          El cliente ha confirmado que la lista de alergias es definitiva
        </label>
        {definitiva && lista.recibida_en && (
          <p className="text-[11px] text-[#5B6B64] pl-6" data-testid="alergenos-recibida-info">
            Marcada por {lista.recibida_por || 'el equipo'} el {formatFechaHora(lista.recibida_en)}
            {lista.correo_id && ` · correo: ${correoVinculado ? (correoVinculado.asunto || '(sin asunto)') : 'en la pestaña Correos'}`}
          </p>
        )}
        <p className="text-xs text-[#5B6B64]">
          Márcalo cuando el cliente te confirme la lista final. Hasta entonces se puede preparar todo, pero la cocina no la da por cerrada.
        </p>
        {definitiva && !verCorreo && correos.length > 0 && (
          <button type="button" onClick={() => { setVerCorreo(true); setCorreoElegido(lista.correo_id || ''); }} className={botonEnlace} data-testid="alergenos-vincular-correo">
            {lista.correo_id ? 'Cambiar el correo vinculado (opcional)' : 'Vincular el correo del cliente (opcional)'}
          </button>
        )}
        {definitiva && verCorreo && (
          <div className="flex flex-col gap-2">
            <select
              value={correoElegido}
              onChange={(e) => setCorreoElegido(e.target.value)}
              className="h-[34px] border border-[#CBD3CF] rounded-lg px-2 text-[13px] w-full"
              aria-label="Correo del cliente con la lista (opcional)"
            >
              <option value="">Sin correo</option>
              {correos.map(c => (
                <option key={c.id} value={c.id}>{(c.asunto || '(sin asunto)').slice(0, 60)}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!!enCurso}
                onClick={async () => { const ok = await handleEstado('recibida', { correo_id: correoElegido || '' }); if (ok) setVerCorreo(false); }}
                className={botonSecundario}
              >
                Guardar
              </button>
              <button type="button" onClick={() => setVerCorreo(false)} className={botonEnlace}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </Tarjeta>
  );

  // ----- Paso 2 -----
  const renderPendientesCocina = () => {
    if (pendientesCocina.length === 0 && confirmadosCocina.length === 0) return null;
    return (
      <Tarjeta titulo="Platos que cocina tiene que confirmar" testid="alergenos-sin-verificar">
        {pendientesCocina.length > 0 && (
          <>
            <p className="text-sm text-[#33443C] mb-3">
              Estos platos los van a comer personas con alergia o dieta, pero el catálogo no tiene revisados todos sus ingredientes (o no tiene ninguno registrado). Pregunta a cocina: si confirman que el plato es seguro, pulsa "Cocina lo confirma" (vale para todo el grupo). Si no, cámbialo por otro plato en el menú del grupo (más abajo).
            </p>
            <ul className="space-y-2" data-testid="alergenos-pendientes-cocina">
              {pendientesCocina.map(b => (
                <li key={b.plato_id} className="flex flex-wrap items-center justify-between gap-2 border border-[#E1E6E3] rounded-lg px-3 py-2">
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-[#16221D]">{b.nombre}</p>
                    <p className="text-xs text-[#33443C]">Para {describirGrupos((b.grupos || []).map(g => g.clave)) || (b.comensales || []).join(', ')}</p>
                    {b.sin_datos && (
                      <p className="text-[11px] font-semibold text-[#92400E]" data-testid="alergenos-sin-datos">{b.texto_sin_datos || 'Sin datos: revisar con cocina'} (para la dieta: el plato no tiene todos sus ingredientes registrados)</p>
                    )}
                    {(b.ingredientes_sin_revisar || []).length > 0 && (
                      <p className="text-[11px] text-[#5B6B64]">Ingredientes sin revisar en el catálogo: {b.ingredientes_sin_revisar.join(', ')}</p>
                    )}
                    {!(b.ingredientes_sin_revisar || []).length && b.motivo && <p className="text-[11px] text-[#5B6B64]">{b.motivo}</p>}
                  </div>
                  <button
                    type="button"
                    disabled={!!enCurso}
                    onClick={() => handleConfirmarCocina(b.plato_id, b.nombre, (b.grupos || []).map(g => g.clave))}
                    className={botonCocina}
                    data-testid="alergenos-confirmar-plato"
                  >
                    {enCurso === `confirmar-${b.plato_id}` ? 'Guardando...' : 'Cocina lo confirma'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {confirmadosCocina.length > 0 && (
          <div className={pendientesCocina.length > 0 ? 'mt-4' : ''} data-testid="alergenos-confirmados-cocina">
            <p className="text-xs font-bold text-[#1E4E79] mb-1">Confirmados a mano por cocina</p>
            <ul className="space-y-1.5 text-xs">
              {confirmadosCocina.map(d => (
                <li key={d.plato_id} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[#16221D]">
                    <strong>{d.nombre}</strong>
                    {' · '}
                    {(d.grupos || []).map(x => `${x.nombre} (${x.usuario || 'sin nombre'}, ${formatFechaHora(x.fecha)})`).join('; ')}
                  </span>
                  <button
                    type="button"
                    disabled={!!enCurso}
                    onClick={() => handleQuitarConfirmacion(d.plato_id, d.nombre, (d.grupos || []).map(x => x.clave))}
                    className={botonEnlace}
                    data-testid="alergenos-quitar-confirmacion"
                  >
                    Quitar confirmación
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Tarjeta>
    );
  };

  const renderLeyenda = () => (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="alergenos-leyenda">
      {['apto', 'contiene', 'trazas', 'dieta', 'sin_verificar', 'confirmado_cocina'].map(k => (
        <span key={k} className={`px-2 py-0.5 rounded border font-semibold ${ESTILO_ESTADO_PLATO[k].clase}`}>{ESTILO_ESTADO_PLATO[k].texto}</span>
      ))}
    </div>
  );

  const renderTablas = () => {
    const indices = (datos.resumen_por_menu || []).map(r => r.menu_index);
    if (indices.length === 0) return null;
    return (
      <Tarjeta
        titulo="Tabla plato por plato"
        accion={(
          <button type="button" onClick={() => setVerTabla(v => !v)} className={botonEnlace} data-testid="alergenos-ver-tabla">
            {verTabla ? 'Ocultar tabla' : 'Ver tabla'}
          </button>
        )}
        testid="alergenos-tabla"
      >
        {!verTabla ? (
          <p className="text-xs text-[#5B6B64]">Todos los platos de cada menú con lo que puede comer cada grupo. Útil para repasarlo con cocina.</p>
        ) : (
          <div className="space-y-5">
            {renderLeyenda()}
            <p className="text-xs md:text-[11px] text-[#5B6B64]">Sin verificar: el catálogo no tiene revisados todos sus ingredientes; cocina tiene que confirmarlo o se cambia por otro plato.</p>
            {indices.map((idx) => {
              const menu = menus[idx] || { platos: [] };
              const columnas = grupos
                .map(g => ({ g, m: (g.por_menu || []).find(x => x.menu_index === idx) }))
                .filter(x => x.m);
              return (
                <div key={idx}>
                  {menus.length > 1 && <p className="text-sm font-bold text-[#16221D] mb-2">{menu.nombre}{menu.hora ? ` · ${menu.hora}` : ''}</p>}
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left font-semibold text-[#5B6B64] px-2 py-1.5 sticky left-0 bg-white">Plato</th>
                          {columnas.map(({ g }) => (
                            <th key={g.clave} className="text-left font-semibold text-[#16221D] px-2 py-1.5 whitespace-nowrap">
                              {g.nombre} ({g.personas || (g.comensales || []).length})
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {menu.platos.map((pl) => (
                          <tr key={pl.id} className="border-t border-[#EEF1EF]">
                            <td className="px-2 py-1.5 text-[#16221D] sticky left-0 bg-white w-[120px] min-w-[120px] max-w-[120px] break-words md:w-auto md:max-w-none md:min-w-[160px]"
                              data-testid="alergenos-matriz-plato">
                              {pl.nombre}
                              {!pl.verificado && <span className="block text-xs md:text-[11px] text-[#5B6B64]">{pl.motivo_sin_verificar}</span>}
                            </td>
                            {columnas.map(({ g, m }) => {
                              const f = (m.platos || []).find(x => x.plato_id === pl.id);
                              if (!f) return <td key={g.clave} className="px-2 py-1.5" />;
                              const estilo = ESTILO_ESTADO_PLATO[f.estado] || ESTILO_ESTADO_PLATO.sin_verificar;
                              const detalle = f.estado === 'dieta'
                                ? textoDietas(f.dietas || [], dietasCatalogo)
                                : (f.alergenos || []).map(id => nombreAlergeno(id, alergenos)).join(', ');
                              const textoEstado = f.sin_datos ? 'Sin datos' : estilo.texto;
                              let titulo = detalle ? `${textoEstado}: ${detalle}` : textoEstado;
                              if (f.estado === 'dieta' && f.motivo_dieta) titulo = f.motivo_dieta;
                              if (f.sin_datos) titulo = 'Sin datos: revisar con cocina';
                              if (f.confirmacion) titulo += ` (confirmado a mano por ${f.confirmacion.usuario || 'cocina'})`;
                              return (
                                <td key={g.clave} className="px-1.5 py-1">
                                  <span
                                    className={`inline-block px-2 py-0.5 rounded border font-semibold whitespace-nowrap ${estilo.clase}`}
                                    title={titulo}
                                    data-testid="alergenos-celda"
                                    data-estado={f.estado}
                                  >
                                    {textoEstado}{detalle ? `: ${detalle}` : ''}
                                  </span>
                                  {f.sustituto_nombre && <span className="block text-xs md:text-[11px] text-[#5B6B64] mt-0.5">Por {f.sustituto_nombre}</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Tarjeta>
    );
  };

  // Una línea de lo que pasa con un plato que el grupo no puede comer tal cual
  const renderDecision = (g, f) => {
    if (f.origen === 'pendiente_verificar') {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[#92400E]">
            {f.sin_datos ? 'Sin datos: revisar con cocina. Se queda en el menú hasta que cocina lo confirme.' : 'Se queda en el menú: falta que cocina lo confirme.'}
          </span>
          <button type="button" disabled={!!enCurso} onClick={() => handleConfirmarCocina(f.plato_id, f.nombre, [g.clave])} className={botonCocina} data-testid="alergenos-confirmar-grupo">
            Cocina lo confirma
          </button>
        </div>
      );
    }
    if (f.sustituto_id) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[#16221D]">Se cambia por <strong>{f.sustituto_nombre}</strong></span>
          {f.sustituto_estado === 'confirmado_cocina' && (
            <span className={`px-1.5 py-0.5 rounded border font-semibold ${ESTILO_ESTADO_PLATO.confirmado_cocina.clase}`}>
              Confirmado a mano por cocina{f.sustituto_confirmacion?.usuario ? ` (${f.sustituto_confirmacion.usuario})` : ''}
            </span>
          )}
          {f.sustituto_estado === 'trazas' && <span className="text-[#92400E]">(trazas aceptadas)</span>}
          {f.sustituto_estado === 'sin_verificar' && (
            <>
              <span className="text-[#92400E]">{f.sustituto_sin_datos ? 'Sin datos: revisar con cocina.' : 'Falta que cocina lo confirme.'}</span>
              <button type="button" disabled={!!enCurso} onClick={() => handleConfirmarCocina(f.sustituto_id, f.sustituto_nombre, [g.clave])} className={botonCocina} data-testid="alergenos-confirmar-grupo">
                Cocina lo confirma
              </button>
            </>
          )}
        </div>
      );
    }
    if (f.origen === 'sin_opciones') {
      return <span className="text-[#92400E]">Sin sustituto: se retira. No hay ningún plato recomendado; puedes elegir otro.</span>;
    }
    return <span className="text-[#33443C]">Sin sustituto: se retira.</span>;
  };

  const renderMenuDeGrupo = (g, menu, mostrarTitulo) => {
    const noAptos = (menu.platos || []).filter(f => !f.puede_comer);
    const mantiene = (menu.platos || []).filter(f => f.puede_comer && f.estado !== 'confirmado_cocina');
    const confirmados = (menu.platos || []).filter(f => f.estado === 'confirmado_cocina');
    return (
      <div key={menu.menu_index} className={mostrarTitulo ? 'border-t border-[#EEF1EF] pt-2' : ''} data-testid="alergenos-menu-grupo">
        {mostrarTitulo && (
          <p className="text-xs font-bold text-[#16221D] mb-1">
            {menu.menu_nombre}{menu.momento ? <span className="font-normal text-[#5B6B64]"> · {menu.momento}</span> : null}
          </p>
        )}
        {mantiene.length > 0 && (
          <p className="text-xs text-[#33443C] mb-2">
            <span className="font-semibold text-[#0F5E43]">Come igual que el resto: </span>
            {mantiene.map(f => f.nombre + (f.estado === 'trazas' ? ' (trazas aceptadas)' : '')).join(', ')}
          </p>
        )}
        {confirmados.length > 0 && (
          <ul className="text-xs text-[#1E4E79] mb-2 space-y-1">
            {confirmados.map(f => (
              <li key={f.plato_id} className="flex flex-wrap items-center gap-2" data-testid="alergenos-plato-confirmado">
                <span className={`px-1.5 py-0.5 rounded border font-semibold ${ESTILO_ESTADO_PLATO.confirmado_cocina.clase}`}>Confirmado a mano por cocina</span>
                <span className="font-semibold text-[#16221D]">{f.nombre}</span>
                <span className="text-[#5B6B64]">
                  {f.confirmacion?.usuario ? `${f.confirmacion.usuario}, ` : ''}{formatFechaHora(f.confirmacion?.fecha)}
                </span>
                <button type="button" disabled={!!enCurso} onClick={() => handleQuitarConfirmacion(f.plato_id, f.nombre, [g.clave])} className={botonEnlace}>
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        )}
        {noAptos.length === 0 ? (
          <p className="text-xs text-[#0F5E43]">Puede comer todo este menú.</p>
        ) : (
          <ul className="space-y-3">
            {noAptos.map((f) => {
              const estilo = ESTILO_ESTADO_PLATO[f.estado] || ESTILO_ESTADO_PLATO.sin_verificar;
              const clave = `${g.clave}|${menu.menu_index}|${f.plato_id}`;
              return (
                <li key={f.plato_id} className="text-xs" data-testid="alergenos-plato-no-apto">
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    <span className={`px-1.5 py-0.5 rounded border font-semibold ${estilo.clase}`}>{f.sin_datos ? 'Sin datos' : estilo.texto}</span>
                    <span className="font-semibold text-[#16221D]">{f.nombre}</span>
                    {(f.alergenos || []).length > 0 && (
                      <span className="text-[#8A1C12]">({f.alergenos.map(id => nombreAlergeno(id, alergenos)).join(', ')})</span>
                    )}
                    {f.estado === 'dieta' && f.motivo_dieta && <span className="text-[#9A3412]">({f.motivo_dieta})</span>}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {renderDecision(g, f)}
                    <button
                      type="button"
                      disabled={!!enCurso}
                      onClick={() => abrirBuscador(g, menu, f.plato_id)}
                      aria-expanded={buscadorAbierto === clave}
                      className={botonEnlace}
                      data-testid="alergenos-selector-sustituto"
                    >
                      {buscadorAbierto === clave ? 'Cerrar' : 'Cambiar plato'}
                    </button>
                  </div>
                  {f.origen === 'propuesta' && f.sustituto_id && (
                    <p className="text-[11px] text-[#5B6B64] mt-0.5" data-testid="alergenos-propuesta-erp">Propuesto por el ERP: puedes cambiarlo.</p>
                  )}
                  {f.origen === 'reemplazada' && <p className="text-[11px] text-[#92400E] mt-0.5">El plato elegido antes ya no le vale: el ERP ha puesto otro.</p>}
                  {f.repetido && f.sustituto_id && (
                    <p className="text-[11px] text-[#92400E] mt-0.5" data-testid="alergenos-sustituto-repetido">Se repite porque no hay más platos aptos.</p>
                  )}
                  {buscadorAbierto === clave && (
                    <BuscadorSustituto
                      fila={f}
                      grupo={g}
                      estadoOpciones={opciones[clave]}
                      deshabilitado={!!enCurso}
                      onElegir={(valor) => handleSustitucion(g, menu, f.plato_id, valor)}
                      onCerrar={() => setBuscadorAbierto(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const renderGrupos = () => {
    if (!hayComensales) return null;
    return (
      <Tarjeta titulo="Menú de cada grupo" testid="alergenos-menus-individuales" guia="alergenos-sustituciones">
        <p className="text-xs text-[#5B6B64] mb-3">
          Los que tienen el mismo perfil (mismos alérgenos, dietas, trazas y menús) comen lo mismo: lo que elijas vale para todo el grupo. Cada persona sigue con su bandeja y su etiqueta en la orden de cocina (o una bandeja de grupo si se ha elegido así).
        </p>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {grupos.map((g) => {
            const hayCambios = (g.por_menu || []).some(m => (m.platos || []).some(f => !f.puede_comer));
            const varios = (g.por_menu || []).length > 1 || menus.length > 1;
            const etiquetas = (g.comensales || []).map(c => c.etiqueta);
            const observaciones = (g.comensales || []).filter(c => c.observaciones);
            const personasGrupo = g.personas || (g.comensales || []).reduce((s, c) => s + personasComensal(c), 0);
            const conBandejaGrupo = (g.comensales || []).filter(c => c.bandeja === 'grupo' && personasComensal(c) > 1);
            return (
              <div key={g.clave} className="border border-[#E1E6E3] rounded-lg p-3 space-y-2" data-testid="alergenos-menu-individual">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#16221D]" data-testid="alergenos-grupo-nombre">
                      {g.nombre} ({personasGrupo}): <span className="font-semibold">{etiquetas.join(', ')}</span>
                    </p>
                    <p className="text-xs text-[#5B6B64]">
                      {((g.alergenos_ids || []).length > 0 || !(g.dietas || []).length) && (
                        <span className="text-[#8A1C12] font-semibold">
                          Sin {(g.alergenos_ids || []).map(id => nombreAlergeno(id, alergenos).toLowerCase()).join(', ') || 'alérgenos marcados'}
                        </span>
                      )}
                      {(g.dietas || []).length > 0 && (
                        <span className="text-[#9A3412] font-semibold">
                          {(g.alergenos_ids || []).length > 0 ? ' · ' : ''}Dieta: {g.dietas_texto || textoDietas(g.dietas, dietasCatalogo)} (sin garantía)
                        </span>
                      )}
                      {' · '}{g.alcance_texto}{g.trazas_aceptables ? ' · acepta trazas' : ''}
                      {g.pendientes > 0 && <span className="text-[#92400E] font-semibold"> · {plural(g.pendientes, 'plato', 'platos')} por confirmar</span>}
                    </p>
                    {(g.avisos_dieta || []).map(t => <div key={t} className="mt-1"><AvisoAmbar texto={t} /></div>)}
                  </div>
                  {hayCambios && (
                    <button
                      type="button"
                      disabled={!!enCurso}
                      onClick={() => handleAleatorio(g)}
                      className="h-[30px] px-3 rounded-lg border border-[#1A9169] text-[#0F5E43] text-xs font-semibold hover:bg-[#E8F5EF] disabled:opacity-50"
                      title="Cambia al azar los platos elegidos por otros que le valen del mismo tipo, de los menús parecidos. Vale para todo el grupo."
                      data-testid="alergenos-aleatorio"
                    >
                      Aleatorio
                    </button>
                  )}
                </div>
                {(g.por_menu || []).map(menu => renderMenuDeGrupo(g, menu, varios))}
                <div className="border-t border-[#EEF1EF] pt-2 text-[11px] text-[#5B6B64]" data-testid="alergenos-etiquetas-grupo">
                  <p>
                    <span className="font-semibold text-[#33443C]">Etiquetas: </span>
                    {etiquetas.join(' · ')}
                    {(g.por_menu || []).length > 1 ? ` (una bandeja por persona en cada momento: ${(g.por_menu || []).map(m => m.menu_nombre).join(', ')})` : ' (una bandeja por persona)'}
                  </p>
                  {conBandejaGrupo.map(c => (
                    <p key={`bg-${c.id}`}>{c.etiqueta}: una bandeja de grupo para {c.cantidad} personas (con las raciones de todos).</p>
                  ))}
                  {observaciones.map(c => <p key={c.id}>{c.etiqueta}: {c.observaciones}</p>)}
                </div>
              </div>
            );
          })}
        </div>
      </Tarjeta>
    );
  };

  // ----- Paso 3 -----
  const renderValidacion = () => {
    const clasePill = {
      sin_lista: 'bg-[#EEF1EF] text-[#4A5A53]',
      recibida: 'bg-[#FDF0DC] text-[#92400E]',
      validada_cocina: 'bg-[#E3F3EC] text-[#0F5E43]',
      sin_alergicos: 'bg-[#E3F3EC] text-[#0F5E43]',
    }[lista.estado] || 'bg-[#EEF1EF] text-[#4A5A53]';
    const historial = [...(lista.historial || [])].reverse();
    return (
      <Tarjeta
        titulo="Validación de cocina"
        testid="alergenos-estado"
        accion={<span className={`px-2.5 py-1 rounded-full text-xs font-bold ${clasePill}`} data-testid="alergenos-estado-pill">{TEXTO_ESTADO_LISTA[lista.estado] || lista.estado}</span>}
      >
        <div className="space-y-3 text-sm text-[#16221D]">
          {validada && (
            <p className="text-[#0F5E43]">
              Validada por cocina el {formatFechaHora(lista.validada_en)}{lista.validada_por ? ` · ${lista.validada_por}` : ''}
            </p>
          )}
          {lista.aviso && (
            <p className="text-[#92400E] bg-[#FDF0DC] rounded-lg px-3 py-2" data-testid="alergenos-aviso-lista">{lista.aviso}</p>
          )}
          {datos.huella_cambiada && (
            <p className="text-[#92400E] bg-[#FDF0DC] rounded-lg px-3 py-2" data-testid="alergenos-huella-cambiada">
              Han cambiado las personas o los menús después de validar con cocina: revisa la lista y vuelve a validarla.
            </p>
          )}
          {sinAlergicos && (
            <p className="text-[#0F5E43]" data-testid="alergenos-validacion-sin-alergicos">
              Sin alérgicos: no hace falta validar con cocina.
            </p>
          )}
          {!validada && !sinAlergicos && (
            <>
              <p className="text-xs text-[#5B6B64]">Pulsa cuando cocina haya revisado el menú de cada grupo. Queda registrado quién y cuándo.</p>
              <button
                type="button"
                disabled={!!enCurso || !datos.puede_validar}
                onClick={() => handleEstado('validar_cocina')}
                className={botonPrincipal}
                data-testid="alergenos-validar"
              >
                {enCurso === 'validar_cocina' ? 'Validando...' : 'Validado por cocina'}
              </button>
            </>
          )}
          {!validada && !sinAlergicos && !datos.puede_validar && (
            <p className="text-xs text-[#92400E]" data-testid="alergenos-no-validable">
              {textoPendientes || 'Faltan platos por confirmar'}: pulsa "Cocina lo confirma" en el paso 2 o cámbialo por otro plato.
            </p>
          )}
          {validada && (
            <button type="button" disabled={!!enCurso} onClick={() => handleEstado('reabrir')} className={botonSecundario} data-testid="alergenos-reabrir">
              {enCurso === 'reabrir' ? 'Guardando...' : 'Reabrir'}
            </button>
          )}
          {historial.length > 0 && (
            <div>
              <button type="button" onClick={() => setVerHistorial(v => !v)} className="text-sm font-semibold text-[#11704F] hover:underline" data-testid="alergenos-ver-historial">
                {verHistorial ? 'Ocultar historial' : `Ver historial (${historial.length})`}
              </button>
              {verHistorial && (
                <ul className="mt-2 space-y-1 text-xs text-[#33443C]" data-testid="alergenos-historial">
                  {historial.map((h, i) => (
                    <li key={`${h.fecha}-${h.accion}-${i}`}>
                      <span className="text-[#5B6B64]">{formatFechaHora(h.fecha)}</span> · {h.usuario} · {h.detalle}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </Tarjeta>
    );
  };

  const renderSuplemento = () => {
    const s = datos.suplemento || {};
    if (!hayComensales) return null;
    let contenido;
    const sinTarifa = s.sin_tarifa || [];
    const nSinTarifa = Number(s.personas_sin_tarifa) || new Set(sinTarifa.map(x => x.comensal_id)).size;
    const avisoSinTarifa = sinTarifa.length > 0 && (
      <p className="text-xs text-[#92400E] mt-1">
        {nSinTarifa} {nSinTarifa === 1 ? 'comensal come' : 'comensales comen'} en algún menú sin tarifa de suplemento ({[...new Set(sinTarifa.map(x => x.menu_nombre))].join(', ')}): ponlo a mano si procede.
      </p>
    );
    // Ya hay un suplemento de dieta puesto a mano: los que solo tienen dieta
    // no se cuentan (para no cobrar dos veces) y queda para revisarlo
    const nDietaMano = Number(s.personas_pendientes_dieta_a_mano) || 0;
    const avisoDietaMano = nDietaMano > 0 && (
      <p className="text-xs text-[#92400E] mt-1" data-testid="alergenos-suplemento-dieta-a-mano">
        Ya hay un suplemento de dieta puesto a mano ({(s.dieta_a_mano || []).join(', ')}): {plural(nDietaMano, 'persona solo con dieta no se cuenta', 'personas solo con dieta no se cuentan')} aquí, para no cobrarlo dos veces. Revisa las líneas.
      </p>
    );
    if (s.multievento) {
      contenido = (
        <p className="px-3 py-2 rounded-lg border bg-[#FDF0DC] border-[#F3D19B] text-xs text-[#92400E]" data-testid="alergenos-suplemento">
          Multievento: el suplemento por menú adaptado se añade a mano en cada jornada ({s.unidades} {s.unidades === 1 ? 'menú adaptado' : 'menús adaptados'}).
        </p>
      );
    } else if (s.regalado) {
      contenido = (
        <div className="text-xs text-[#5B6B64]" data-testid="alergenos-suplemento-regalado">
          Suplemento por menú adaptado regalado por {s.regalado.usuario || s.regalado.usuario_email || 'el equipo'} el {formatFechaHora(s.regalado.fecha).slice(0, 10)}
          {s.regalado.unidades != null ? ` (${s.regalado.unidades} ${s.regalado.unidades === 1 ? 'menú' : 'menús'}, ${formatEuros(s.regalado.importe)})` : ''}.
          {s.regalado.unidades != null && s.regalado.unidades !== s.unidades && ` Ahora hay ${s.unidades}.`}
          {' '}
          <button type="button" disabled={!!enCurso} onClick={() => handleSuplemento('quitar_regalo')} className="underline hover:text-[#16221D] disabled:opacity-50">Deshacer</button>
          {avisoSinTarifa}
          {avisoDietaMano}
        </div>
      );
    } else if ((s.lineas || []).length > 0) {
      contenido = (
        <div className="text-xs text-[#5B6B64]" data-testid="alergenos-suplemento-aplicado">
          Suplemento por menú adaptado incluido: {(s.lineas || []).map(l => `${l.unidades} × ${formatEuros(l.precio_unitario)}`).join(' + ')}. Se ajusta solo si cambian los comensales.
          {avisoSinTarifa}
          {avisoDietaMano}
        </div>
      );
    } else if (!s.unidades) {
      if (!avisoSinTarifa && !avisoDietaMano) return null;
      contenido = <div data-testid="alergenos-suplemento">{avisoSinTarifa}{avisoDietaMano}</div>;
    } else {
      contenido = (
        <div className="px-3 py-3 rounded-lg border bg-[#FDF0DC] border-[#F3D19B] text-sm text-[#92400E]" data-testid="alergenos-suplemento">
          <p className="font-semibold">
            {(s.por_formato || []).map(f => `${f.unidades} × ${formatEuros(f.tarifa)} (${f.formato_nombre})`).join(' + ')} = {formatEuros(s.importe)}
          </p>
          <p className="text-xs mt-0.5">Uno por persona con alergia o dieta y formato en el que come (si desayuna y toma el café de la tarde, el coffee cuenta una vez), con IVA del 10 %. Todavía no está en las líneas del presupuesto.</p>
          {avisoSinTarifa}
          {avisoDietaMano}
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" disabled={!!enCurso} onClick={() => handleSuplemento('anadir')} className={botonPrincipal} data-testid="alergenos-suplemento-anadir">
              {enCurso === 'sup-anadir' ? 'Añadiendo...' : 'Añadir suplemento'}
            </button>
            <button type="button" disabled={!!enCurso} onClick={() => handleSuplemento('regalar')} className={botonSecundario} data-testid="alergenos-suplemento-regalar">
              {enCurso === 'sup-regalar' ? 'Guardando...' : 'Regalar suplemento'}
            </button>
          </div>
        </div>
      );
    }
    return <Tarjeta titulo="Suplemento por menú adaptado">{contenido}</Tarjeta>;
  };

  // Pregunta inicial: sin nada apuntado, primero "¿Hay alérgicos?". Con
  // comensales, lista recibida o validada, adaptaciones en los menús o
  // adaptaciones sin comensal, directamente la pantalla completa.
  const avisoTexto = aviso && (
    <p
      className={`text-sm rounded-lg px-3 py-2 ${aviso.tipo === 'error' ? 'bg-[#FDE8E6] text-[#8A1C12]' : 'bg-[#E3F3EC] text-[#0F5E43]'}`}
      role={aviso.tipo === 'error' ? 'alert' : 'status'}
      data-testid="alergenos-aviso"
    >
      {aviso.texto}
    </p>
  );
  const sinNadaApuntado = !hayComensales && !hayAdaptacionesEnMenus && lista.estado === 'sin_lista'
    && !(Number(datos.huerfanas) > 0);
  if (sinAlergicos && !hayComensales) {
    return (
      <div className="space-y-4" data-testid="pestana-alergenos">
        {avisoTexto}
        <div className="rounded-xl border-2 border-[#1A9169] bg-[#E3F3EC] px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="alergenos-sin-alergicos-pantalla">
          <div className="min-w-0">
            <p className="text-lg font-bold text-[#0F5E43]">No hay alérgicos ni dietas</p>
            <p className="text-sm text-[#33443C]">
              El cliente ha confirmado que no hay alérgicos
              {lista.sin_alergicos_por ? `: marcado por ${lista.sin_alergicos_por}` : ''}
              {lista.sin_alergicos_en ? ` el ${formatFechaHora(lista.sin_alergicos_en)}` : ''}.
            </p>
          </div>
          <button type="button" disabled={!!enCurso}
            onClick={() => { setHayAlergicosElegido(false); handleEstado('reabrir'); }}
            className={botonSecundario} data-testid="alergenos-deshacer-sin-alergicos">
            {enCurso === 'reabrir' ? 'Guardando...' : 'Deshacer'}
          </button>
        </div>
      </div>
    );
  }
  if (sinNadaApuntado && !hayAlergicosElegido) {
    return (
      <div className="space-y-4" data-testid="pestana-alergenos">
        {avisoTexto}
        {alergiaSinRegistrar && (
          <div className="text-sm rounded-lg px-3 py-2 bg-[#FDF0DC] text-[#92400E]" data-testid="alergenos-observaciones-sin-registrar">
            <p className="font-semibold">{TEXTO_ALERGIA_SIN_REGISTRAR}</p>
            <p className="mt-1 whitespace-pre-line">{textoObservacionesPresupuesto(presupuesto)}</p>
          </div>
        )}
        <div className="rounded-xl border border-[#CBD3CF] bg-white px-5 py-5" data-testid="alergenos-pregunta">
          <p className="text-lg font-bold text-[#16221D]">¿Hay alérgicos o dietas especiales en este evento?</p>
          <p className="text-sm text-[#5B6B64] mt-1">
            Si el cliente ha comunicado alergias o dietas (vegetariano, vegano, sin cerdo, embarazada, sin lactosa), se apuntan y se prepara el menú de cada grupo con cocina.
            Si no hay (o no las ha comunicado), la tarea queda hecha.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" disabled={!!enCurso} onClick={() => setHayAlergicosElegido(true)}
              className="h-11 px-5 rounded-lg border-2 border-[#C9870F] bg-[#FDF0DC] text-[#7A3A06] font-bold hover:bg-[#FBE3BD] disabled:opacity-50"
              data-testid="alergenos-pregunta-si" data-uso="alergenos.hay_alergicos">
              Sí, hay alérgicos o dietas
            </button>
            <button type="button" disabled={!!enCurso} onClick={() => handleEstado('sin_alergicos')}
              className="h-11 px-5 rounded-lg bg-[#1A9169] hover:bg-[#11704F] text-white font-bold disabled:opacity-50"
              data-testid="alergenos-pregunta-no" data-uso="alergenos.sin_alergicos">
              {enCurso === 'sin_alergicos' ? 'Guardando...' : 'No hay alérgicos ni dietas'}
            </button>
          </div>
          <p className="text-xs text-[#5B6B64] mt-3">{TEXTO_GARANTIA}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pestana-alergenos">
      {renderPasos()}
      {aviso && (
        <p
          className={`text-sm rounded-lg px-3 py-2 ${aviso.tipo === 'error' ? 'bg-[#FDE8E6] text-[#8A1C12]' : 'bg-[#E3F3EC] text-[#0F5E43]'}`}
          role={aviso.tipo === 'error' ? 'alert' : 'status'}
          data-testid="alergenos-aviso"
        >
          {aviso.texto}
        </p>
      )}
      {alergiaSinRegistrar && (
        <div className="text-sm rounded-lg px-3 py-2 bg-[#FDF0DC] text-[#92400E]" data-testid="alergenos-observaciones-sin-registrar">
          <p className="font-semibold">{TEXTO_ALERGIA_SIN_REGISTRAR}</p>
          <p className="mt-1 whitespace-pre-line">{textoObservacionesPresupuesto(presupuesto)}</p>
        </div>
      )}
      {Number(datos.huerfanas) > 0 && (
        <div className="text-sm rounded-lg px-3 py-2 bg-[#FDF0DC] text-[#92400E] flex flex-wrap items-center justify-between gap-2" data-testid="alergenos-huerfanas">
          <p>
            {datos.huerfanas === 1
              ? 'Hay 1 adaptación de alérgenos sin comensal (en la cabecera o en una jornada): sigue restando raciones en cocina y compras.'
              : `Hay ${datos.huerfanas} adaptaciones de alérgenos sin comensal (en la cabecera o en las jornadas): siguen restando raciones en cocina y compras.`}
          </p>
          <button type="button" disabled={!!enCurso} onClick={handleLimpiarHuerfanas} className={botonSecundario} data-testid="alergenos-limpiar-huerfanas">
            {enCurso === 'limpiar' ? 'Limpiando...' : 'Limpiar adaptaciones sin comensal'}
          </button>
        </div>
      )}
      {(datos.avisos || []).length > 0 && (
        <div className="text-sm rounded-lg px-3 py-2 bg-[#FDF0DC] text-[#92400E]" data-testid="alergenos-avisos">
          {datos.avisos.map(t => <p key={t}>{t}</p>)}
        </div>
      )}

      <section id="alergenos-paso-1" className="space-y-3 scroll-mt-4" data-testid="alergenos-paso-1">
        {renderCabeceraPaso(1, 'Alergias y dietas', 'Las personas con alergias o dietas que ha comunicado el cliente')}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 min-w-0">{renderComensales()}</div>
          <div className="min-w-0">{renderListaDefinitiva()}</div>
        </div>
      </section>

      {hayComensales && (
        <section id="alergenos-paso-2" className="space-y-3 scroll-mt-4" data-testid="alergenos-paso-2">
          {renderCabeceraPaso(2, 'Menú de cada grupo', 'Los de la misma dieta comen lo mismo; cada uno con su bandeja')}
          {(datos.resumen_por_menu || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="alergenos-resumen">
              {datos.resumen_por_menu.map(r => (
                <span key={r.menu_index} className="text-sm bg-white border border-[#E1E6E3] rounded-lg px-3 py-2 text-[#16221D]">
                  {menus.length > 1 && <strong>{r.nombre}: </strong>}{r.texto}
                </span>
              ))}
            </div>
          )}
          {renderPendientesCocina()}
          {renderGrupos()}
          {renderTablas()}
        </section>
      )}

      <section id="alergenos-paso-3" className="space-y-3 scroll-mt-4" data-testid="alergenos-paso-3">
        {renderCabeceraPaso(3, 'Confirmación de cocina', 'Cocina revisa los menús y se deja registrado')}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="min-w-0">{renderValidacion()}</div>
          <div className="min-w-0">{renderSuplemento()}</div>
        </div>
      </section>
    </div>
  );
};

export default PestanaAlergenos;
